import { useMemo } from 'react';
import { css, type Theme } from '@emotion/react';
import type { AvatarProps } from 'client/components/Other/Avatar';
import { ListItemFigure, type ListItemFigureProps } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';
import { Skeleton } from 'client/components/Skeleton/Skeleton';
import { borderRadius } from 'client/Theme';

export const CatalogItemSkeleton = () => {
  return <Skeleton height={76} animate css={skeletonStyle} />;
};

export interface CatalogItemProps {
  id: string;
  title: string | JSX.Element;
  subtitle?: string | JSX.Element | null;
  icon?: JSX.Element;
  ornamentIcon?: JSX.Element | null;
  ornamentState?: ListItemFigureProps['state'];
  image?: AvatarProps;

  /** Render action or actions at the right end of the item */
  action?: JSX.Element;

  /** Prevent text from wrapping to a new line */
  noWrap?: boolean;
}

export const CatalogItem = ({
  id,
  title,
  subtitle = null,
  icon,
  ornamentIcon = null,
  ornamentState,
  image,
  action,
  noWrap = false,
  ...other
}: CatalogItemProps) => {
  const labelID = useMemo(() => `catalog-${id}-label`, [id]);

  return (
    <li key={id} css={catalogListItemStyle} {...other}>
      <ListItemFigure
        image={image}
        icon={icon ? <icon.type size={32} {...icon.props} /> : null}
        ornament={ornamentIcon}
        state={ornamentState}
        css={figureStyle}
      />
      <ListItemText
        id={labelID}
        primary={title}
        secondary={subtitle}
        primaryProps={{ noWrap }}
        secondaryProps={{ component: 'span', noWrap }}
      />
      {!!action && <div css={actionsStyle}>{action}</div>}
    </li>
  );
};

const catalogListItemStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  margin-top: -2px;
  margin-bottom: 0;
  padding: 10px 16px;
  min-height: 72px;
  border: 2px solid ${theme.color.grey[60]};
  box-sizing: border-box;

  figure {
    margin-right: 16px;

    .ornament {
      > :not(:last-of-type) {
        color: ${theme.color.grey[60]};
        transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      }
    }
  }

  &:first-of-type {
    border-top-left-radius: ${borderRadius}px;
    border-top-right-radius: ${borderRadius}px;
  }

  &:last-of-type {
    border-bottom-left-radius: ${borderRadius}px;
    border-bottom-right-radius: ${borderRadius}px;
  }
`;

const actionsStyle = css`
  display: flex;
  gap: 8px;
  position: relative;
  flex-shrink: 0;
`;

const figureStyle = css`
  margin-right: 0.25rem;
`;

const skeletonStyle = (theme: Theme) => css`
  background-color: ${theme.color.grey[60]};
  border-radius: 0;

  &:first-of-type {
    border-radius: ${borderRadius}px ${borderRadius}px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
  }
`;
